import sum from 'utils/sum';
import Team from 'records/Team';
import {List, Map, Record, Set} from 'immutable';
import ExecDashboardSource from 'sources/ExecDashboardSource';
import UserActions from 'actions/UserActions';
import Theme from 'styles/theme';
import AdminFilterWordProfileSource from 'sources/admin/AdminFilterWordProfileSource';

class StatPair extends Record({
  id: undefined,
  context: undefined,
}) {
  constructor(id, context) {
    super({id, context});
  }
}

export const TOPIC_ROLLUP_BAR_COLOR = Theme.palette.pastelGood;

/**
 * Fake team representing the summary tab.
 */
export const TOPIC_ROLLUP_SUMMARY_TEAM = new Team({
  id: 'total', // not a UUID, so cannot represent a real team.
  name: 'Summary',
});

/**
 * Enumeration of topic rollup subtabs types.
 *
 * @type {Readonly<{PERCENT: string, COUNT: string}>}
 */
export const TOPIC_ROLLUP_SUBTABS = Object.freeze({
  PERCENT: 'Percent',
  COUNT: 'Count',
});

/** Make the trend chart label. */
export function makeChartLabel(name, title) {
  return `${name} - ${title}`;
}

/**
 * Extract ids from stats.
 * @param stats {List<{id: any}>} list of objects, each having an id.
 * @returns {Immutable.Set<any>} a set of ids found in the given stats.
 */
export function idsFromStats(stats) {
  return stats.map(obj => obj.id).toSet();
}

/**
 * Calculate a percentage.
 *
 * @param numerator {number}
 * @param denominator {number}
 * @returns {number} the percentage, or NaN if denominator is 0.
 */
export function percentage(numerator, denominator) {
  return denominator === 0 ? NaN : (numerator / denominator) * 100;
}

/**
 * Build topic rollup data by team for the summary tab and its top-level drill down.
 */
export function topicRollupData(triggerIdToName, conversations, topicStats) {
  const statsTriggerIds = topicStats
    .map(stat => stat.context)
    // Remove any not relevant to this display. This can include removing stats for inactive
    // triggers. Note that we have to convert the string context to a number for lookup.
    .filter(triggerId => triggerIdToName.get(Number(triggerId)))
    .toSet();

  // this will accumulate our results
  const countByTriggerId = {};
  const percentByTriggerId = {};

  statsTriggerIds.forEach(triggerId => {
    // accumulate by triggerId
    const byThisTriggerId = topicStats.filter(stat => stat.context === triggerId);
    // set totals.
    countByTriggerId[triggerId] = sum(byThisTriggerId.map(t => t.value));
  });

  // Set the total percentage, now that we have the total for each topic.
  const totalCalls = sum(conversations.valueSeq());
  for (const topic of Object.keys(countByTriggerId)) {
    percentByTriggerId[topic] = percentage(countByTriggerId[topic], totalCalls);
  }

  // IMPORTANT: summary stats CANNOT have an id, or the trends will break. The trends container
  // interprets any id in stats as a team id, and we have no team ids here.
  const bySubtab = {};
  bySubtab[TOPIC_ROLLUP_SUBTABS.COUNT] = List(
    Object.keys(countByTriggerId).map(id => ({
      value: countByTriggerId[id],
      context: id,
    }))
  );
  bySubtab[TOPIC_ROLLUP_SUBTABS.PERCENT] = List(
    Object.keys(percentByTriggerId).map(id => ({
      value: percentByTriggerId[id],
      context: id,
    }))
  );

  return Map(bySubtab);
}

/**
 * Fetch team trigger analytics stats.
 *
 * @param startDate start date of the date range.
 * @param endDate end date of the date range.
 * @param teamId id of the team to fetch stats for.
 * @param triggerId optional triggerId. Will only return stats for this trigger if provided.
 * Returns them all otherwise.
 * @param triggerIdToName {Immutable.Map<number, string>} map of trigger id to its name. Used to
 * filter stats (stats for triggers not in this map are not returned).
 * @param includePercentages {boolean} set to true to include data for the percentages subtab. This
 * is more expensive, so you should only include percentages when needed.
 * @returns {Promise<[Map, List]>} promise resolving to a Map of subtab name to stats for that
 * subtab and a list of users in the team.
 */
export async function fetchTeamStats({
  startDate,
  endDate,
  teamId,
  triggerId,
  triggerIdToName,
  includePercentages,
}) {
  // Get all the triggerIds so we can generate stats for missing users.
  // They have to be strings, because they're going to go into the context field.
  const allTriggerIds = triggerId
    ? Set([triggerId.toString()])
    : triggerIdToName
        .keySeq()
        .map(triggerId => triggerId.toString())
        .toSet();

  const statsPromise = ExecDashboardSource.getUserTopicMetrics({
    teams: [teamId],
    startDate,
    endDate,
  }).then(stats =>
    // TODO ideally the database could do this for us.
    stats.filter(stat => !triggerId || stat.context === triggerId)
  );

  const conversationsPromise = ExecDashboardSource.getUserConversations({
    teams: [teamId],
    startDate,
    endDate,
  });

  return Promise.all([
    statsPromise,
    includePercentages ? conversationsPromise : Promise.resolve(Map()),
    UserActions.fetchAll({teamId}),
  ])
    .then(([stats, conversations, users]) => {
      // Stats API doesn't bother with extra joins to send us zeroes for users with no stats. We have
      // to add those ourselves.
      const filteredStats = stats.filter(stat => allTriggerIds.has(stat.context));
      const presentPairs = filteredStats.map(stat => new StatPair(stat.id, stat.context)).toSet();
      const allPairs = users
        .flatMap(user => allTriggerIds.map(triggerId => new StatPair(user.userId, triggerId)))
        .toSet();
      const missingPairs = allPairs.subtract(presentPairs);
      const missing = missingPairs.map(({id: userId, context: triggerId}) => ({
        id: userId,
        value: 0,
        context: triggerId,
      }));
      const adjustedStats = List([...filteredStats, ...missing]);
      return [adjustedStats, conversations, users];
    })
    .then(([stats, conversations, users]) => {
      // Now we can build the map of subtabId to list of stats we need for each subtab.
      const statsMap = {};
      statsMap[TOPIC_ROLLUP_SUBTABS.COUNT] = stats;
      // Conversations will be empty if the caller didn't ask for percentages. Avoid generating
      // garbage and/or blowing up if it wasn't requested.
      if (includePercentages) {
        statsMap[TOPIC_ROLLUP_SUBTABS.PERCENT] = stats.map(stat => ({
          ...stat,
          value: percentage(stat.value, conversations.get(stat.id)),
        }));
      }
      return [Map(statsMap), users];
    });
}

/**
 * Fetch teams/departments using profiles associated with filter words used in trigger rules with
 * the given ids.
 *
 * @param triggerRuleIds the trigger rule ids.
 * @returns {Promise<*>}
 */
export async function fetchProfileUsageByTriggerRuleId(triggerRuleIds) {
  return Promise.all([
    AdminFilterWordProfileSource.fetchProfileByTriggerRuleUsage(triggerRuleIds),
    AdminFilterWordProfileSource.fetchDepartments(),
  ]).then(([usageMap, departments]) => {
    const departmentsByProfileId = departments
      .filter(department => department.filterWordProfileId)
      .groupBy(department => department.filterWordProfileId);
    const teamsByProfileId = departments
      .flatMap(department =>
        department.teams.filter(
          team =>
            team.filterWordProfileId && team.filterWordProfileId !== department.filterWordProfileId
        )
      )
      .groupBy(team => team.filterWordProfileId);

    return usageMap.map(filterWordProfileIds => {
      const departmentNames = filterWordProfileIds
        .flatMap(filterWordProfileId => departmentsByProfileId.get(filterWordProfileId))
        .map(d => d.name)
        .sort();
      const teamNames = filterWordProfileIds
        .flatMap(filterWordProfileId => teamsByProfileId.get(filterWordProfileId))
        .map(t => t.name)
        .sort();

      return {departmentNames, teamNames};
    });
  });
}
